import type { ArchetypeDef, MotivationDef, OriginDef } from './schema';

/**
 * Origins are the circumstance the player did not choose. They matter — a lot,
 * early — but none of them determines the ending. Each has a compensating
 * strength so no origin is strictly worse than another.
 */
const ORIGIN_LIST: OriginDef[] = [
  {
    id: 'origin.steady',
    label: 'A steady household',
    blurb: 'No wealth, no crisis. Two parents who worried about money quietly.',
    effects: [
      { k: 'cash', amount: 3200 },
      { k: 'fixedExpenses', mode: 'set', value: 11800 },
    ],
    traitBias: { conscientiousness: 6, patience: 4 },
    statBias: { health: 3 },
    tags: ['balanced'],
  },
  {
    id: 'origin.comfortable',
    label: 'Comfortable and expectant',
    blurb: 'University was assumed. So was a certain kind of success.',
    effects: [
      { k: 'cash', amount: 14000 },
      { k: 'fixedExpenses', mode: 'set', value: 14200 },
      { k: 'skill', skill: 'communication', delta: 8 },
      { k: 'lifestyle', delta: 2 },
    ],
    traitBias: { ambition: 8, socialConfidence: 8, resilience: -6 },
    tags: ['advantaged', 'pressure'],
  },
  {
    id: 'origin.scarce',
    label: 'Scarce and self-reliant',
    blurb: 'You learned to fix things, stretch things, and expect nothing.',
    effects: [
      { k: 'cash', amount: 400 },
      { k: 'fixedExpenses', mode: 'set', value: 9600 },
      { k: 'debt', op: 'add', kind: 'consumer', label: 'Family obligation', amount: 4200, rate: 0.09, years: 8 },
      { k: 'skill', skill: 'selfManagement', delta: 10 },
      { k: 'skill', skill: 'technical', delta: 4 },
    ],
    traitBias: { resilience: 14, discipline: 8, riskTolerance: -5, optimism: -4 },
    statBias: { stress: 8 },
    tags: ['disadvantaged', 'resilient'],
  },
  {
    id: 'origin.immigrant',
    label: 'A new country',
    blurb: 'Everything is possible and nothing is familiar. You arrived hungry.',
    effects: [
      { k: 'cash', amount: 1800 },
      { k: 'fixedExpenses', mode: 'set', value: 10400 },
      { k: 'skill', skill: 'social', delta: 6 },
      { k: 'skill', skill: 'selfManagement', delta: 6 },
    ],
    traitBias: { ambition: 12, adaptability: 12, resilience: 8, socialConfidence: -6 },
    tags: ['ambitious', 'adaptable'],
  },
  {
    id: 'origin.rural',
    label: 'A small town',
    blurb: 'Everyone knew you. Leaving would cost something; staying would too.',
    effects: [
      { k: 'cash', amount: 2600 },
      { k: 'fixedExpenses', mode: 'set', value: 8200 },
      { k: 'skill', skill: 'technical', delta: 6 },
      { k: 'relationshipAll', dim: 'closeness', delta: 10 },
    ],
    traitBias: { empathy: 8, patience: 8, noveltySeeking: -6 },
    statBias: { health: 5, happiness: 4 },
    tags: ['connected', 'limited-options'],
  },
  {
    id: 'origin.fractured',
    label: 'A fractured start',
    blurb: 'Adults who could not hold it together. You held yourself together instead.',
    effects: [
      { k: 'cash', amount: 900 },
      { k: 'fixedExpenses', mode: 'set', value: 9900 },
      { k: 'skill', skill: 'social', delta: 4 },
      { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: -22 },
      { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: 22 },
    ],
    traitBias: { resilience: 12, conflictAvoidance: 10, optimism: -8 },
    statBias: { stress: 12, happiness: -6 },
    tags: ['disadvantaged', 'independent'],
  },
];

export const ORIGINS: Record<string, OriginDef> = Object.fromEntries(ORIGIN_LIST.map((o) => [o.id, o]));
export const ORIGIN_DEF_LIST: readonly OriginDef[] = ORIGIN_LIST;

/**
 * Motivations set the player's declared starting aspirations. They are a
 * statement of intent, not a class — a player who starts out chasing money can
 * end up a caregiver, and the ending report will notice.
 */
const MOTIVATION_LIST: MotivationDef[] = [
  {
    id: 'motive.security',
    label: 'I want to feel safe',
    blurb: 'Runway, a home, no one to owe.',
    aspirations: ['goal.emergencyFund', 'dream.ownHome'],
    traitBias: { patience: 6, riskTolerance: -8, conscientiousness: 6 },
    tags: ['security'],
  },
  {
    id: 'motive.build',
    label: 'I want to build something',
    blurb: 'A company, a body of work, something with your fingerprints on it.',
    aspirations: ['dream.business', 'goal.mastery'],
    traitBias: { ambition: 10, riskTolerance: 10, discipline: 4 },
    tags: ['entrepreneurship'],
  },
  {
    id: 'motive.family',
    label: 'I want people around me',
    blurb: 'A partner, children, a table that is always full.',
    aspirations: ['dream.family', 'goal.closeFriends'],
    traitBias: { empathy: 10, socialConfidence: 6, ambition: -4 },
    tags: ['family'],
  },
  {
    id: 'motive.freedom',
    label: 'I want to be free',
    blurb: 'Own your calendar. Answer to no one. Go anywhere.',
    aspirations: ['dream.financialIndependence', 'dream.travel'],
    traitBias: { noveltySeeking: 12, adaptability: 8, patience: -4 },
    tags: ['freedom'],
  },
  {
    id: 'motive.meaning',
    label: 'I want the work to matter',
    blurb: 'Be useful. Leave the place better than you found it.',
    aspirations: ['dream.contribute', 'dream.craft'],
    traitBias: { empathy: 8, curiosity: 8, ambition: -2 },
    tags: ['meaning'],
  },
  {
    id: 'motive.excel',
    label: 'I want to be exceptional',
    blurb: 'Top of the field. Whatever the field turns out to be.',
    aspirations: ['goal.seniorRole', 'goal.mastery'],
    traitBias: { ambition: 14, discipline: 8, empathy: -4 },
    tags: ['career'],
  },
];

export const MOTIVATIONS: Record<string, MotivationDef> = Object.fromEntries(
  MOTIVATION_LIST.map((m) => [m.id, m]),
);
export const MOTIVATION_DEF_LIST: readonly MotivationDef[] = MOTIVATION_LIST;

/**
 * Archetypes are *interpretations*, scored after the fact from what the life
 * actually contained. They are intentionally overlapping; the highest score
 * wins, and the narrative never claims the player chose it.
 */
const ARCHETYPE_LIST: ArchetypeDef[] = [
  {
    id: 'arch.builder',
    label: 'The Builder',
    narrative:
      '{name} made things. Not always the right things, and rarely on schedule, but the world ended up with more in it than when they arrived. They measured a year by what stood at the end of it.',
    score: [
      { path: 'ending.legacy', weight: 1.2 },
      { path: 'counter.creativeWorks', weight: 1.4 },
      { path: 'ending.purpose', weight: 0.8 },
      { path: 'career.promotions', weight: 0.5 },
    ],
    epitaphs: ['Left more than they took.', 'Built while others waited.'],
  },
  {
    id: 'arch.entrepreneur',
    label: 'The Entrepreneur',
    narrative:
      '{name} could not leave a good idea alone. They put their own money on the table more than once, learned exactly what failure costs, and kept going anyway.',
    score: [
      { path: 'flag.founder', weight: 1.5 },
      { path: 'business.exits', weight: 1.1 },
      { path: 'ending.financial', weight: 0.6 },
      { path: 'business.failures', weight: 0.5 },
    ],
    requires: [{ k: 'flag', flag: 'founder' }],
    epitaphs: ['Bet on themselves.', 'Risked it, more than once.'],
  },
  {
    id: 'arch.caregiver',
    label: 'The Caregiver',
    narrative:
      '{name} organised their life around other people. It cost them promotions and probably a decade of compounding. The people are still here, and they still call.',
    score: [
      { path: 'ending.relationships', weight: 1.6 },
      { path: 'family.childrenBorn', weight: 1.1 },
      { path: 'ending.purpose', weight: 0.6 },
      { path: 'trait.empathy', weight: 0.5 },
    ],
    epitaphs: ['Everyone’s person.', 'Held the family together.'],
  },
  {
    id: 'arch.strategist',
    label: 'The Strategist',
    narrative:
      '{name} played the long game deliberately. Few dramatic moves, almost no panic, and a compounding advantage that only became obvious in the final third.',
    score: [
      { path: 'ending.financial', weight: 1.0 },
      { path: 'habit.investing', weight: 0.9 },
      { path: 'ending.resilience', weight: 0.5 },
      { path: 'trait.patience', weight: 0.8 },
    ],
    epitaphs: ['Patient, and right.', 'Compounded quietly.'],
  },
  {
    id: 'arch.explorer',
    label: 'The Explorer',
    narrative:
      '{name} kept changing the question. Careers, cities, disciplines — they traded the safety of a single track for a life with more rooms in it.',
    score: [
      { path: 'career.pivots', weight: 1.3 },
      { path: 'counter.travelTrips', weight: 1.2 },
      { path: 'trait.noveltySeeking', weight: 0.7 },
      { path: 'ending.growth', weight: 0.6 },
    ],
    epitaphs: ['Never stayed still.', 'Collected lives, not things.'],
  },
  {
    id: 'arch.survivor',
    label: 'The Survivor',
    narrative:
      '{name} was handed more than their share of bad years. They lost work, money, and people — and were still standing at the end, which is not a small thing.',
    score: [
      { path: 'ending.resilience', weight: 1.3 },
      { path: 'counter.crises', weight: 1.3 },
      { path: 'career.layoffs', weight: 0.6 },
      { path: 'finance.bankruptcies', weight: 0.7 },
    ],
    requires: [{ k: 'num', path: 'counter.crises', op: '>=', value: 2 }],
    epitaphs: ['Knocked down, never out.', 'Survived what should have ended them.'],
  },
  {
    id: 'arch.dreamer',
    label: 'The Dreamer',
    narrative:
      '{name} always had a plan for next year. Some of it happened. A lot of it stayed a plan, and they knew it, and they made peace with it late.',
    score: [
      { path: 'ending.regret', weight: 1.2 },
      { path: 'inertia.decayedCount', weight: 1.3 },
      { path: 'inertia.deferredCount', weight: 0.7 },
      { path: 'trait.optimism', weight: 0.4 },
    ],
    epitaphs: ['Meant to, always.', 'Dreamed in more detail than most people live.'],
  },
  {
    id: 'arch.balanced',
    label: 'The Balanced Life',
    narrative:
      '{name} never topped a single chart. They were solvent, reasonably healthy, genuinely loved, and mildly annoyed about work — which, on the evidence, is close to the best available outcome.',
    score: [
      { path: 'ending.balance', weight: 1.9 },
      { path: 'ending.happiness', weight: 0.7 },
    ],
    epitaphs: ['Got the proportions right.', 'Nothing at the extremes, everything in place.'],
  },
  {
    id: 'arch.lateBloomer',
    label: 'The Late Bloomer',
    narrative:
      '{name} spent decades looking like someone who had missed their moment. Then the last third arrived and rearranged the whole story.',
    score: [
      { path: 'ending.lateGrowth', weight: 1.7 },
      { path: 'career.pivots', weight: 0.6 },
    ],
    requires: [{ k: 'num', path: 'age', op: '>=', value: 58 }],
    epitaphs: ['Started late, finished well.', 'The best years were the last ones.'],
  },
  {
    id: 'arch.specialist',
    label: 'The Specialist',
    narrative:
      '{name} picked one thing and went all the way down. They were the person you called when it really mattered, and they paid for that depth in breadth.',
    score: [
      { path: 'career.reputation', weight: 1.0 },
      { path: 'skill.max', weight: 1.1 },
      { path: 'career.roleLevel', weight: 0.9 },
      { path: 'ending.purpose', weight: 0.4 },
    ],
    epitaphs: ['Unarguably good at it.', 'Depth over breadth, deliberately.'],
  },
  {
    id: 'arch.provider',
    label: 'The Provider',
    narrative:
      '{name} turned work into security for other people. The sacrifice was real and mostly invisible, and the household never once went short.',
    score: [
      { path: 'ending.financial', weight: 0.8 },
      { path: 'family.childrenBorn', weight: 1.2 },
      { path: 'finance.lifetimeEarnings.log', weight: 0.7 },
      { path: 'ending.relationships', weight: 0.5 },
    ],
    requires: [{ k: 'num', path: 'childCount', op: '>=', value: 1 }],
    epitaphs: ['Carried the household.', 'Made sure no one else had to worry.'],
  },
];

export const ARCHETYPES: Record<string, ArchetypeDef> = Object.fromEntries(
  ARCHETYPE_LIST.map((a) => [a.id, a]),
);
export const ARCHETYPE_DEF_LIST: readonly ArchetypeDef[] = ARCHETYPE_LIST;
